/**
 * Cart Utilities
 * Shared cart integration functions for all product purchase components
 * Extracted from the former separate-buy-buttons-v4-no-bulk.liquid (removed 2026-09-10)
 */

window.CartUtilities = window.CartUtilities || {};

(function(CartUtilities) {
  'use strict';

  // Configuration - All settings and constants
  const CONFIG = {
    // Selling Plans
    STAY_SELLING_PLAN_ID: document.querySelector('[data-stay-selling-plan-id]')?.dataset.staySellingPlanId || '689099145512',
    ADDON_SUBSCRIPTION_PLAN_ID: document.querySelector('[data-addon-plan-id]')?.dataset.addonPlanId || null,
    CROSS_SELL_PLAN_ID: document.querySelector('[data-cross-sell-plan-id]')?.dataset.crossSellPlanId || '2882076770',
    TRYNOW_SELLING_PLAN_ID: document.querySelector('[data-trynow-selling-plan-id]')?.dataset.trynowSellingPlanId || '691435766056',
    TRYNOW_CHECKOUTPRODUCT_VARIANT_ID: document.querySelector('[data-trynow-checkout-product-variant-id]')?.dataset.trynowCheckoutProductVariantId || '50633931555112',

    // Product Properties
    PROPERTIES: {
      FULL_PRICE: "_full_price",
      PRODUCT_TYPE: "_product_type",
      SUBSCRIPTION_PURCHASE: "_is_subscription_purchase",
      ONE_TIME_PURCHASE: "_is_one_time_purchase",
      SELLING_PLAN: "_selling_plan",
      TRY_NOW_PAIR_KEY: "_trynow_pair_key",
      TRY_NOW_PAIR_ROLE: "_trynow_pair_role",
      OTP_DISCOUNT_CODE: "_otp_discount_code",
      SUB_DISCOUNT_CODE: "_sub_discount_code",
      AUTO_APPLY_OTP_DISCOUNT_CODE: "_auto_apply_otp_discount_code",
      OTP_DISCOUNT_PERCENTAGE: "_otp_discount_percentage",
      SUB_DISCOUNT_PERCENTAGE: "_sub_discount_percentage",
      // Plan a line *would* convert to if upgraded to subscription. Stamped on
      // OTP lines too, so the "_if_upgrade_" prefix keeps it from reading as an
      // active subscription binding (the real binding is item.selling_plan).
      IF_UPGRADE_SELLING_PLAN_ID: "_if_upgrade__selling_plan_id",
    },
    PAIR_ROLES: {
      PRIMARY: 'primary',
      SUBSCRIPTION: 'subscription'
    }
  };

  /**
   * Create item data structure for cart
   * @param {Object} params - Item parameters
   * @param {number} params.variantId - Shopify variant ID
   * @param {number} params.quantity - Item quantity
   * @param {boolean} params.isSubscription - Whether this is a subscription purchase
   * @param {boolean} [params.isTBYB=false] - Whether this is a Try Before You Buy item
   * @param {Object} [params.properties={}] - Additional line item properties
   * @param {string|null} [params.sellingPlanId=null] - Override selling plan ID
   * @param {boolean} [params.isAddon=false] - Whether this is an add-on product
   * @returns {Object} Cart item data structure
   */
  function createItemData({variantId, quantity, isSubscription, isTBYB = false, properties = {}, sellingPlanId = null, upgradeSellingPlanId = null, isAddon = false}) {
    const baseProperties = {
      [CONFIG.PROPERTIES.FULL_PRICE]: "true",
      [CONFIG.PROPERTIES.PRODUCT_TYPE]: "rebrand",
      ...properties
    };

    // Stamp PDP discount metadata so cart.json downstream readers (minicart pricing,
    // upgrade-to-sub flow) can recover the original codes/percentages without a
    // separate /products/{handle}.js round-trip. Only stamps when __pdpDiscountOverride
    // is published by the PDP — non-override pages get no noise.
    const pdpOverride = (typeof window !== 'undefined' && window.__pdpDiscountOverride) || null;
    if (pdpOverride) {
      const otpCode = pdpOverride.otpCouponCode || pdpOverride.couponCode || '';
      const subCode = pdpOverride.couponCode || '';
      if (otpCode) baseProperties[CONFIG.PROPERTIES.OTP_DISCOUNT_CODE] = String(otpCode);
      if (subCode) baseProperties[CONFIG.PROPERTIES.SUB_DISCOUNT_CODE] = String(subCode);
      const isUpsell = properties && (properties._is_upsell === 'true' || properties._is_upsell === true);
      if (pdpOverride.autoApplyOnOtp === true && otpCode && !isSubscription && !isAddon && !isTBYB && !isUpsell) {
        baseProperties[CONFIG.PROPERTIES.AUTO_APPLY_OTP_DISCOUNT_CODE] = 'true';
      }
      if (Number.isFinite(pdpOverride.discountPercent)) {
        baseProperties[CONFIG.PROPERTIES.OTP_DISCOUNT_PERCENTAGE] = String(pdpOverride.discountPercent);
      }
      // Prefer the section's own sub_discount_percentage (frequency selector hidden input)
      // so the line item remembers the exact sub discount configured on the PDP it was
      // added from, even when the PDP has no separate OTP discount code. A latched
      // funnel offer on a catalog PDP wins over the Liquid-baked global %.
      const offerOverridesCatalog = typeof window !== 'undefined' &&
        window.FDOffer &&
        typeof window.FDOffer.overridesCatalogPdp === 'function' &&
        window.FDOffer.overridesCatalogPdp(window.location && window.location.pathname);
      const sectionSubPctEl = (!offerOverridesCatalog && typeof document !== 'undefined')
        ? document.getElementById('PBFCM-sub-discount-percentage')
        : null;
      const sectionSubPctRaw = sectionSubPctEl ? parseFloat(sectionSubPctEl.value) : NaN;
      const sectionSubPct = (Number.isFinite(sectionSubPctRaw) && sectionSubPctRaw > 0 && sectionSubPctRaw <= 100)
        ? sectionSubPctRaw
        : null;
      const subPct = sectionSubPct !== null
        ? sectionSubPct
        : (Number.isFinite(pdpOverride.subDiscountPercent)
            ? pdpOverride.subDiscountPercent
            : (Number.isFinite(pdpOverride.discountPercent) ? pdpOverride.discountPercent : null));
      if (subPct !== null) {
        baseProperties[CONFIG.PROPERTIES.SUB_DISCOUNT_PERCENTAGE] = String(subPct);
      }
    }

    const item = {
      id: variantId,
      quantity: quantity,
      properties: baseProperties
    };

    const resolvedSellingPlanId = sellingPlanId ||
                                  (isSubscription ? (isAddon ? CONFIG.ADDON_SUBSCRIPTION_PLAN_ID : CONFIG.STAY_SELLING_PLAN_ID) : null);

    if (resolvedSellingPlanId) {
      item.selling_plan = resolvedSellingPlanId;
    }

    // Record the plan this line *would* convert to if upgraded to subscription,
    // even for OTP items. Prefer an explicit upgradeSellingPlanId (OTP box / sibling
    // frequency option). Sub items get their actual plan. Upgrade metadata only —
    // the active subscription binding is item.selling_plan above, not this property.
    const planForProperty = resolvedSellingPlanId ||
                            upgradeSellingPlanId ||
                            (isAddon ? CONFIG.ADDON_SUBSCRIPTION_PLAN_ID : CONFIG.STAY_SELLING_PLAN_ID);
    if (planForProperty) {
      item.properties[CONFIG.PROPERTIES.IF_UPGRADE_SELLING_PLAN_ID] = String(planForProperty);
    }

    if (isSubscription) {
      item.properties[CONFIG.PROPERTIES.SUBSCRIPTION_PURCHASE] = true;
    }

    if (!isSubscription && !isTBYB) {
      item.properties[CONFIG.PROPERTIES.ONE_TIME_PURCHASE] = true;
    }

    if (isTBYB) {
      item.selling_plan = CONFIG.TRYNOW_SELLING_PLAN_ID;
      item.properties[CONFIG.PROPERTIES.ONE_TIME_PURCHASE] = true;
      item.properties[CONFIG.PROPERTIES.SELLING_PLAN] = CONFIG.TRYNOW_SELLING_PLAN_ID;
      item.properties[CONFIG.PROPERTIES.IF_UPGRADE_SELLING_PLAN_ID] = String(CONFIG.TRYNOW_SELLING_PLAN_ID);
    }

    return item;
  }

  /**
   * Add items to cart
   * @param {Object} itemsData - Cart items data with items array
   * @returns {Promise<void>}
   */
  async function addToCartWithItems(itemsData) {
    const pageAdd = typeof updateCartDisplay === 'function' ? beginPageAdd([]) : null;
    try {
      const response = await window.addToCart(itemsData);
      const data = await response.json();

      if (!response.ok) {
        const error = new Error(data.description || data.message || 'Failed to add product to cart');
        error.data = data;
        throw error;
      }

      if (typeof updateCartDisplay !== 'function') {
        window.location.href = '/cart';
        return;
      }

      await confirmPageAdd(pageAdd);

      if (typeof showAddToCartMessage === 'function') {
        showAddToCartMessage('Product added to cart!');
      }
    } catch (error) {
      console.error('🛒 [CART-UTIL] Error adding to cart:', error);
      if (pageAdd) {
        pageAdd.fail(error);
      } else if (typeof showAddToCartMessage === 'function') {
        showAddToCartMessage('Error adding product to cart', 'error');
      } else {
        alert('Error adding product to cart');
      }
    }
  }

  // The drawer placeholder state started at the click, or one started now.
  function beginPageAdd(lines) {
    const pageAdd = window.FDCartPageAdd;
    if (!pageAdd) return null;
    return pageAdd.current() || pageAdd.begin(lines);
  }

  function focusDrawerAfterOpen() {
    setTimeout(() => {
      const checkoutButton = document.getElementById('checkoutButton');

      if (checkoutButton && !checkoutButton.disabled) {
        checkoutButton.focus();
        if (typeof announceToScreenReader === 'function') {
          announceToScreenReader('Items added to cart. Focus on checkout button.', 'polite');
        }
      } else {
        const cart = document.querySelector('#main-nav');
        const closeButton = cart?.querySelector('.js-menu__close');
        if (closeButton) closeButton.focus();
      }
    }, 150);
  }

  // One cart read after the add, one paint of the confirmed cart.
  async function confirmPageAdd(pageAdd) {
    if (typeof toggleCartOpen === 'function') {
      toggleCartOpen();
      focusDrawerAfterOpen();
    }
    const updatedCart = window.FDCartApi
      ? await window.FDCartApi.getCart(true)
      : await fetch('/cart.js').then(r => r.json());

    if (pageAdd) {
      await pageAdd.confirm(updatedCart);
    } else {
      updateCartDisplay(updatedCart);
    }

    if (typeof updateCartCount === 'function') {
      updateCartCount(updatedCart.item_count);
    }
    return updatedCart;
  }

  /**
   * Update cart display after adding items
   * @returns {Promise<void>}
   */
  async function updateCartAfterAdd(startedAdd) {
    if (typeof updateCartDisplay !== 'function') return;

    const pageAdd = startedAdd || beginPageAdd([]);
    try {
      await confirmPageAdd(pageAdd);
    } catch (error) {
      console.error('🛒 [CART-UTIL] Failed to fetch/render cart after opening drawer:', error);
      if (pageAdd) pageAdd.fail(error);
    }
  }

  /**
   * Add cross-sell items to items array
   * @param {Array} items - Current items array
   * @param {boolean} isSubscription - Whether main items are subscription
   * @param {boolean} [isTBYB=false] - Whether this is a TBYB order
   * @returns {Promise<Array>} Updated items array with cross-sell items
   */
  async function addCrossSellItems(items, isSubscription, isTBYB = false) {
    const crossSellChecked = document.getElementById('crossSellProduct')?.checked;

    if (crossSellChecked) {
      const crossSellId = document.getElementById('crossSellId').value;
      const crossSellPlan = CONFIG.CROSS_SELL_PLAN_ID;

      const crossSellPairId = `cs-${Date.now()}-${crossSellId}`;

      items.forEach(item => {
        if (!item || typeof item !== 'object') {
          return;
        }

        const itemProperties = item.properties || {};
        item.properties = {
          ...itemProperties,
          _cross_sell_pair_id: crossSellPairId,
          _has_cross_sell: 'true'
        };
      });

      // Add the main cross-sell item (with TBYB flag if applicable)
      items.push(createItemData({
        variantId: crossSellId,
        quantity: 1,
        isSubscription: isTBYB ? false : isSubscription,  // Force false for TBYB primary item
        isTBYB: isTBYB,
        sellingPlanId: (!isTBYB && isSubscription) ? crossSellPlan : null,
        properties: {
          _is_cross_sell: 'true',
          _cross_sell_pair_id: crossSellPairId
        }
      }));

    }

    return items;
  }

  /**
   * Get current product ID from various sources
   * @returns {string|number|null} Product ID or null
   */
  function getCurrentProductId() {
    return window.meta?.product?.id ||
      window.ShopifyAnalytics?.meta?.product?.id ||
      document.querySelector('[data-product-id]')?.dataset?.productId;
  }

  // Expose public API
  CartUtilities.createItemData = createItemData;
  CartUtilities.addToCartWithItems = addToCartWithItems;
  CartUtilities.updateCartAfterAdd = updateCartAfterAdd;
  CartUtilities.addCrossSellItems = addCrossSellItems;
  CartUtilities.getCurrentProductId = getCurrentProductId;
  CartUtilities.CONFIG = CONFIG;

})(window.CartUtilities);
